import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Хук для управления уведомлениями с автоматическим скрытием через таймаут.
 * @param timeout - Время в миллисекундах до автоматического скрытия уведомления (по умолчанию 3000).
 */
export function useNotification(timeout = 3000) {
    const [notificationList, setNotificationList] = useState([]);
    const idRef = useRef(0);
    const timersRef = useRef({});
    /**
     * Удаляет уведомление по ID и очищает связанный таймер.
     */
    const removeNotification = useCallback((id) => {
        setNotificationList((list) => list.filter((n) => n.id !== id));
        if (timersRef.current[id]) {
            clearTimeout(timersRef.current[id]);
            delete timersRef.current[id];
        }
    }, []);
    /**
     * Добавляет новое уведомление и устанавливает таймер для его автоматического удаления.
     */
    const addNotification = useCallback((message, type) => {
        idRef.current += 1;
        const id = idRef.current;
        setNotificationList((list) => [...list, { message, type, id }]);
        timersRef.current[id] = setTimeout(() => removeNotification(id), timeout);
        return id;
    }, [timeout, removeNotification]);
    /**
     * Показывает информационное уведомление.
     */
    const showNotification = useCallback((message) => addNotification(message, 'info'), [addNotification]);
    /**
     * Показывает уведомление об ошибке.
     */
    const showError = useCallback((message) => addNotification(message, 'error'), [addNotification]);
    /**
     * Скрывает уведомление по ID или первое в списке, если ID не указан.
     */
    const hideNotification = useCallback((id) => {
        if (id !== undefined) {
            removeNotification(id);
        }
        else if (notificationList.length > 0) {
            removeNotification(notificationList[0].id);
        }
    }, [notificationList, removeNotification]);
    // Очищаем все таймеры при размонтировании компонента
    useEffect(() => () => {
        Object.values(timersRef.current).forEach(clearTimeout);
        timersRef.current = {};
    }, []);
    return { notificationList, showNotification, showError, hideNotification, timeout };
}
